import { Injectable, OnDestroy, inject } from "@angular/core";
import { Store } from "@ngxs/store";
import * as EventLogStateActions from "@vp/data-access/event-logs";
import { EventLogFilter } from "@vp/data-access/event-logs";
import { OrganizationState } from "@vp/data-access/organization";
import { UserApiService } from "@vp/data-access/users";
import { UiSchemaConfigService, UiSchemaLayoutProvider } from "@vp/formly/ui-schema-config";
import {
  AssignableRoles,
  AssignableTagTypes,
  AssignedRolePerDepartment,
  Department,
  LayoutConfigOption,
  Organization,
  Role,
  RoleAccessTagsItem,
  Snippet,
  Tag,
  TagType,
  TagsArray,
  User,
  UserRole,
  UserTypeConfig
} from "@vp/models";
import { NotificationService } from "@vp/shared/notification-service";
import { filterNullMap } from "@vp/shared/operators";
import { deepCopy, deeperCopy, mergeDeep, stringArraysEqual } from "@vp/shared/utilities";
import { JSONSchema7 } from "json-schema";
import { Operation, createPatch } from "rfc6902";
import { EMPTY, Observable, Subject, throwError } from "rxjs";
import { map, takeUntil, withLatestFrom } from "rxjs/operators";
import * as UserAdministrationActions from "../state+/user-administration.actions";
import { UserAdministrationState } from "../state+/user-administration.state";
import { AssignableRolesService } from "./assignable-roles-service";

@Injectable()
export class UserAdministrationService implements OnDestroy {
  organization$: Observable<Organization | null> = inject(Store).select(
    OrganizationState.organization
  );
  user$: Observable<User | null> = inject(Store).select(UserAdministrationState.user);
  workingCopy$: Observable<User | null> = inject(Store).select(UserAdministrationState.workingCopy);
  userAssignableTags$: Observable<Tag[]> = inject(Store).select(
    UserAdministrationState.assignableTags
  );
  pendingOperations$: Observable<Operation[]> = inject(Store).select(
    UserAdministrationState.pendingOperations
  );

  private readonly _destroyed$ = new Subject<void>();

  constructor(
    private readonly store: Store,
    private readonly userApiService: UserApiService,
    private assignableRolesService: AssignableRolesService,
    private readonly uiSchemaConfigService: UiSchemaConfigService,
    private readonly notificationService: NotificationService,
    private readonly uiSchemaLayoutProvider: UiSchemaLayoutProvider
  ) {}

  initalize() {
    this.workingCopy$
      .pipe(
        withLatestFrom(this.organization$.pipe(filterNullMap())),
        takeUntil(this._destroyed$),
        map(([user, organization]: [User | null, Organization]) => {
          if (user == null) return;

          const assignableTagTypes: TagType[] = this.getAssignableTagTypes(
            user,
            organization.departments,
            organization.tagTypes
          );

          this.store.dispatch(
            new UserAdministrationActions.SetAssignableEntities(assignableTagTypes)
          );
        })
      )
      .subscribe();
  }

  getAssignableTagTypes = (
    user: User,
    departments: Department[],
    tagTypes: TagType[]
  ): TagType[] => {
    const userDepartmentIds: Set<string> = new Set<string>();
    user.roles.forEach(r => r.departments.forEach(d => userDepartmentIds.add(d.departmentId)));
    const userDepartments: Department[] = departments.filter(d =>
      userDepartmentIds.has(d.departmentId)
    );
    const assignableTagTypeFriendlyIds: Set<string> = new Set<string>();
    userDepartments.forEach(d =>
      d.assignableTagTypes
        .filter(
          (assignableTagTypes: AssignableTagTypes) =>
            assignableTagTypes.userType === user.userType.friendlyId
        )
        .forEach((assignableTagTypes: AssignableTagTypes) =>
          assignableTagTypes.tagTypes?.forEach((tagTypeFriendlyId: string) =>
            assignableTagTypeFriendlyIds.add(tagTypeFriendlyId)
          )
        )
    );

    return tagTypes.filter((tagType: TagType) =>
      assignableTagTypeFriendlyIds.has(tagType.friendlyId)
    );
  };

  layoutSchema$ = (userTypeFriendlyId: string): Observable<JSONSchema7> => {
    const organization = this.store.selectSnapshot(OrganizationState.organization);
    if (!organization) return EMPTY;

    const userTypeConfig: UserTypeConfig | undefined = organization.userTypeConfig.find(
      c => c.type == userTypeFriendlyId
    );
    if (userTypeConfig === undefined) return EMPTY;
    const layoutConfig: LayoutConfigOption = userTypeConfig.userLayout;
    this.uiSchemaConfigService.addScopedConfig(
      layoutConfig,
      `user-admininstration-${userTypeFriendlyId}`
    );
    const schema: JSONSchema7 = userTypeConfig.userSchema;
    return this.uiSchemaLayoutProvider.applyScopes(
      "userAdminComponent",
      schema,
      `user-admininstration-${userTypeFriendlyId}`
    );
  };

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
    this.resetUser();
  }

  resetUser() {
    this.store.dispatch(new UserAdministrationActions.ResetState());
    this.store.dispatch(new EventLogStateActions.ResetState());
  }

  setUser(id: string) {
    this.store.dispatch(new UserAdministrationActions.SetUser(id));
  }

  loadUserEventLogs(filter: Partial<EventLogFilter>) {
    this.store.dispatch(new EventLogStateActions.GetUserEventLogs(filter));
  }

  loadUser(user: User) {
    this.store.dispatch(new UserAdministrationActions.LoadUser(user));
  }

  commit() {
    const userId = this.store.selectSnapshot(UserAdministrationState.getWorkingCopy)?.userId;
    const pendingOperations = this.store.selectSnapshot(UserAdministrationState.pendingOperations);

    if (!userId || pendingOperations.length === 0) return EMPTY;

    return this.userApiService.patch(userId, pendingOperations, "user_adminUserSave");
  }

  invite(resendInvite?: boolean) {
    const workingCopy = this.store.selectSnapshot(UserAdministrationState.getWorkingCopy);
    if (!workingCopy) {
      return EMPTY;
    }

    if (!workingCopy.roles || workingCopy.roles.length === 0) {
      throw Error(
        "Failed to resend invitation. Please assign one or more department & role(s) to User"
      );
    }

    return this.userApiService.inviteUser(workingCopy, resendInvite);
  }

  create() {
    const workingCopy = this.store.selectSnapshot(UserAdministrationState.getWorkingCopy);
    if (!workingCopy) {
      return EMPTY;
    }

    if (!workingCopy.roles || workingCopy.roles.length === 0) {
      return throwError(
        () =>
          new Error(
            `Please assign one or more department & role(s) to ${workingCopy.userType.displayName}`
          )
      );
    }

    return this.userApiService.createUser(workingCopy);
  }

  clone(userId: string) {
    this.userApiService
      .getUser(userId)
      .pipe(
        filterNullMap(),
        withLatestFrom(
          this.assignableRolesService.roleWithConditionalAccess$,
          this.assignableRolesService.allowedRolesPerDepartment$
        )
      )
      .subscribe(
        ([userCloningFrom, assignableRoles, assignedRoles]: [
          User,
          AssignableRoles | null,
          AssignedRolePerDepartment[]
        ]) => {
          if (userCloningFrom.roles?.length === 0) {
            this.notificationService.warningMessage(
              "No roles available to clone based on your Roles permitted"
            );
            return;
          }

          const roles = assignableRoles
            ? userCloningFrom.roles.filter(r => assignedRoles.map(a => a.roleId).includes(r.roleId))
            : userCloningFrom.roles;

          const clonedData = {
            roles: roles,
            assignedTags: userCloningFrom.assignedTags
          } as Partial<User>;

          if (userCloningFrom.userPermissions?.length > 0) {
            clonedData.userPermissions = userCloningFrom.userPermissions;
          }

          this.updateWorkingCopy(clonedData);
        }
      );
  }

  updateWorkingCopy = (partialUser: Partial<User>) => {
    const original = this.store.selectSnapshot(UserAdministrationState.user);
    const workingCopy = this.store.selectSnapshot(UserAdministrationState.getWorkingCopy);
    const modified = mergeDeep(workingCopy, partialUser, "replace", true);

    if (!original || !workingCopy) return;

    const operations = createPatch(original, modified);

    this.store.dispatch(new UserAdministrationActions.UpdateWorkingCopy(modified));
    this.store.dispatch(new UserAdministrationActions.SetPendingOperations(operations));
  };

  addSingleDepartmentRole(role: AssignedRolePerDepartment) {
    const original = this.store.selectSnapshot(UserAdministrationState.user);
    const workingCopy = this.store.selectSnapshot(UserAdministrationState.getWorkingCopy);
    const organization = this.store.selectSnapshot(OrganizationState.organization);

    if (!original || !workingCopy || !organization) return;

    const modified = addDepartmentRole(
      workingCopy,
      role.roleId,
      role.departmentId,
      organization.roles,
      organization.departments
    );

    const operations = createPatch(original, modified);
    this.store.dispatch(new UserAdministrationActions.UpdateWorkingCopy(modified));
    this.store.dispatch(new UserAdministrationActions.SetPendingOperations(operations));
  }

  addDepartmentRoles(roles: AssignedRolePerDepartment[]) {
    const original = this.store.selectSnapshot(UserAdministrationState.user);
    const workingCopy = this.store.selectSnapshot(UserAdministrationState.getWorkingCopy);
    const organization = this.store.selectSnapshot(OrganizationState.organization);

    if (!original || !workingCopy || !organization) return;

    const modified = addDepartmentRoles(
      workingCopy,
      roles,
      organization.roles,
      organization.departments
    );

    const operations = createPatch(original, modified);
    this.store.dispatch(new UserAdministrationActions.UpdateWorkingCopy(modified));
    this.store.dispatch(new UserAdministrationActions.SetPendingOperations(operations));
  }

  deleteDepartmentRole(roleId: string, departmentId: string) {
    const original = this.store.selectSnapshot(UserAdministrationState.user);
    const workingCopy = this.store.selectSnapshot(UserAdministrationState.getWorkingCopy);

    if (!original || !workingCopy) return;

    const modified = deleteDepartmentByRole(workingCopy, roleId, departmentId);
    const operations = createPatch(original, modified);

    this.store.dispatch(new UserAdministrationActions.UpdateWorkingCopy(modified));
    this.store.dispatch(new UserAdministrationActions.SetPendingOperations(operations));
  }

  addAccessTags(roleId: string, tagIds: string[], compoundAssignmentEnabled: boolean) {
    const original = this.store.selectSnapshot(UserAdministrationState.user);
    const workingCopy = this.store.selectSnapshot(UserAdministrationState.getWorkingCopy);

    if (!original || !workingCopy) return;

    const modified = addAccessTags(workingCopy, roleId, tagIds, compoundAssignmentEnabled);
    const operations = createPatch(original, modified);

    this.store.dispatch(new UserAdministrationActions.UpdateWorkingCopy(modified));
    this.store.dispatch(new UserAdministrationActions.SetPendingOperations(operations));
  }

  deleteAccessTags(item: RoleAccessTagsItem) {
    const original = this.store.selectSnapshot(UserAdministrationState.user);
    const workingCopy = this.store.selectSnapshot(UserAdministrationState.getWorkingCopy);

    if (!original || !workingCopy) return;

    const modified = deleteUserRoleAccessTags(workingCopy, item);
    const operations = createPatch(original, modified);

    this.store.dispatch(new UserAdministrationActions.UpdateWorkingCopy(modified));
    this.store.dispatch(new UserAdministrationActions.SetPendingOperations(operations));
  }

  addOrEditSnippet(action: "Add" | "Edit", snippet: Snippet, index?: number) {
    const original = this.store.selectSnapshot(UserAdministrationState.user);
    const workingCopy = this.store.selectSnapshot(UserAdministrationState.getWorkingCopy);

    if (!original || !workingCopy) return;

    const modified = addOrEditSnippet(workingCopy, action, snippet, index);
    const operations = createPatch(original, modified);

    this.store.dispatch(new UserAdministrationActions.UpdateWorkingCopy(modified));
    this.store.dispatch(new UserAdministrationActions.SetPendingOperations(operations));
  }

  deleteSnippet(index: number) {
    const original = this.store.selectSnapshot(UserAdministrationState.user);
    const workingCopy = this.store.selectSnapshot(UserAdministrationState.getWorkingCopy);

    if (!original || !workingCopy) return;

    const modified = deleteSnippet(workingCopy, index);
    const operations = createPatch(original, modified);

    this.store.dispatch(new UserAdministrationActions.UpdateWorkingCopy(modified));
    this.store.dispatch(new UserAdministrationActions.SetPendingOperations(operations));
  }
}

const addDepartmentRole = (
  user: User,
  userRoleId: string,
  userDepartmentId: string,
  roles: Role[],
  departments: Department[]
) => {
  const copy = deeperCopy(user);
  const role = copy.roles.find((role: Role) => role.roleId === userRoleId);
  const deptFriendlyId = departments.find(d => d.departmentId === userDepartmentId)?.friendlyId;
  if (role) {
    role.departments.push({
      departmentId: userDepartmentId,
      friendlyId: deptFriendlyId
    });
  } else {
    const roleFriendlyId = roles.find(r => r.roleId === userRoleId)?.friendlyId;
    if (!roleFriendlyId) return;
    copy.roles.push({
      roleId: userRoleId,
      friendlyId: roleFriendlyId,
      departments: [
        {
          departmentId: userDepartmentId,
          friendlyId: deptFriendlyId
        }
      ]
    });
  }
  return copy;
};

const addDepartmentRoles = (
  user: User,
  userRoles: AssignedRolePerDepartment[],
  roles: Role[],
  departments: Department[]
) => {
  return userRoles.reduce(
    (acc: User, currRole: AssignedRolePerDepartment) =>
      addDepartmentRole(acc, currRole.roleId, currRole.departmentId, roles, departments),
    user
  );
};

const addAccessTags = (
  user: User,
  roleId: string,
  tagIds: string[],
  compoundAssignmentEnabled: boolean
): User => {
  const copy = deeperCopy(user);
  const userRole: UserRole = copy.roles.find((r: UserRole) => r.roleId === roleId);
  const tagsArray: TagsArray = { tags: tagIds };
  if (!userRole) throw Error("User has no roles.");
  if (!Array.isArray(userRole.accessTags)) {
    userRole["accessTags"] = [];
  }
  if (compoundAssignmentEnabled) {
    userRole.accessTags.push(tagsArray);
  } else {
    tagIds.forEach(tagId => {
      userRole.accessTags?.push({ tags: [tagId] });
    });
  }

  return copy;
};

const deleteDepartmentByRole = (user: User, roleId: string, departmentId: string) => {
  const copy: User = deepCopy(user);
  const role = copy.roles.find(r => r.roleId === roleId);
  if (role) {
    role.departments = role?.departments.filter(d => d.departmentId !== departmentId);
    if (role.departments.length === 0) {
      copy.roles = copy.roles.filter(r => r.roleId !== roleId);
    }
  }
  return copy;
};

const deleteUserRoleAccessTags = (user: User, item: RoleAccessTagsItem) => {
  const copy: User = deepCopy(user);
  const role: UserRole | undefined = copy.roles.find(r => r.roleId === item.roleId);
  if (role) {
    const accessTags: TagsArray | undefined = role.accessTags?.find(accessTags =>
      stringArraysEqual(accessTags.tags, item.tagIds)
    );
    if (accessTags) {
      role.accessTags = role.accessTags?.filter(
        accessTags => stringArraysEqual(accessTags.tags, item.tagIds) === false
      );
    }
  }
  return copy;
};

const addOrEditSnippet = (
  user: User,
  action: "Add" | "Edit",
  snippet: Snippet,
  index?: number
): User => {
  const copy: User = deepCopy(user);
  if (!copy.userData) return copy;

  switch (action) {
    case "Add":
      copy.userData = {
        ...copy.userData,
        snippets: copy.userData?.snippets ? copy.userData?.snippets?.concat(snippet) : [snippet]
      };
      break;

    case "Edit":
      if (index !== undefined && copy.userData.snippets) {
        copy.userData.snippets[index] = snippet;
      }
      break;
  }
  return copy;
};

const deleteSnippet = (user: User, index: number) => {
  const copy: User = deepCopy(user);
  if (!copy.userData) return copy;
  if (copy.userData.snippets) {
    copy.userData.snippets.splice(index, 1);
  }
  return copy;
};
